import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { cn } from '@divlab/divanui';

import InteriorImage from '@Components/InteriorImage';
import ApiProduct from '@Api/Product';
import { useDeps } from '@Contexts/DI';
import styles from './InteriorImageContainer.module.css';

import type { ProductData } from '@Types/Product';
import type { InteriorImageContentData } from '@Pages/PageWikiArticle';
import type { InteriorImageProps } from '@Components/InteriorImage';
import type { FC, MutableRefObject } from 'react';

export interface InteriorImageContainerProps extends Omit<InteriorImageProps, 'img'> {
  className?: string;
  contentData: InteriorImageContentData;
  analyticsTitle: string;
  refGrid: MutableRefObject<HTMLDivElement>;
}

const InteriorImageContainer: FC<InteriorImageContainerProps> = (props) => {
  const { className, contentData, analyticsTitle, refGrid, ...restProps } = props;
  const [products, setProducts] = useState<ProductData[]>([]);
  const { logger } = useDeps();

  const img = useMemo(() => {
    const item = { src: contentData.image, pins: [] };
    contentData.marks.forEach((mark) => {
      const product = products.find((elem) => elem.id === mark.id);
      if (!product) return;

      item.pins.push({
        coords: Object.values(mark.coords),
        product,
      });
    });

    return item;
  }, [contentData, products]);

  const allPinProductsIds = useMemo(() => {
    const productIds: number[] = [];

    contentData.marks.forEach((mark) => {
      productIds.push(mark.id);
    });

    return Array.from(new Set(productIds));
  }, [contentData.marks]);

  const load = useCallback(async () => {
    try {
      if (allPinProductsIds.length === 0) return;

      const res = await ApiProduct.getProducts(allPinProductsIds);

      if (res.length === 0) {
        throw Error("Product wasn't loaded");
      }
      setProducts(res);
    } catch (err) {
      logger.log(err);
    }
  }, [allPinProductsIds, logger]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div {...restProps} className={cn(styles.interiorImageContainer, className)}>
      <InteriorImage
        className={styles.interiorImage}
        img={img}
        analyticsTitle={analyticsTitle}
        refGrid={refGrid}
      />
    </div>
  );
};

export default memo(InteriorImageContainer);
